<template>
  <div>
    <PerspectiveLegend :perspectives="actualPerspectives" :all-perspectives="allPerspectives" />
    <div class="mt-1 flex w-full flex-row">
      <div
        v-for="(bar, barIndex) in bars"
        :key="bar.scaleItem.value"
        class="flex min-w-0 flex-1 flex-col items-center"
      >
        <div class="mb-1.5 flex h-28 w-full flex-row items-center gap-0.5 px-1">
          <div
            v-for="(value, valueIndex) in bar.values"
            :key="value.perspective"
            class="flex h-full min-w-0 grow flex-col items-center justify-end"
          >
            <span class="text-label-medium h-4">
              {{ value.count }}
            </span>
            <div
              class="ease-fast-spatial rounded-b-extra-small w-full transition-all duration-400"
              :class="[
                value.count > 0 && colorClassesForPerspective(value.perspective, allPerspectives),
                value.count === 0 && 'bg-surface-container-highest',
                bar.values.length === 1 && 'rounded-t-large',
                bar.values.length > 1 && 'rounded-t-small',
              ]"
              :style="{
                height: barHeight(value.count),
                transitionDelay: (valueIndex + barIndex) * 100 + 'ms',
              }"
            />
          </div>
        </div>
        <div
          class="text-body-small text-outline border-outline-variant w-full px-0.5 text-center wrap-break-word hyphens-auto"
        >
          <span>{{ bar.scaleItem.label }}</span>
        </div>
      </div>
    </div>
    <div class="flex w-full flex-row justify-center pt-2">
      <PerspectiveValueBarNew
        :is-center-based="scale.type === ScaleType.Slider && scale.isOptimumPoint"
        :hide-labels="true"
        :values="valueBarPerspectives"
        :all-perspectives="allPerspectives"
        :disable-animation="disableAnimation"
        :scale="scale.items.map((item) => ({ value: item.value, label: item.label ?? '' }))"
        class="w-full"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { ScaleType, type Scale } from '@/core/models/scale'
  import { useHistogramBars } from '@/core/composables/use-histogram-bars'
  import type {
    DistributedPerspectiveAnalysis,
    PulsePerspectiveAnalysis,
  } from '@/core/models/surveys-analytics'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'
  import {
    analysisPerspectiveKey,
    toValueBarPerspectives,
  } from '@/core/models/perspective-analysis'
  import { colorClassesForPerspective } from '@/core/models/feedback-perspective.ts'
  import PerspectiveLegend from '../question-list/PerspectiveLegend.vue'
  import PerspectiveValueBarNew from '../question-list/PerspectiveValueBarNew.vue'

  interface Props {
    scale: Scale
    perspectives: ReadonlyArray<DistributedPerspectiveAnalysis | PulsePerspectiveAnalysis>
    allPerspectives: ReadonlyArray<Perspective>
    disableAnimation?: boolean
    /** When true, self is drawn as a bar series. Defaults to false. */
    showSelfFeedback?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    showSelfFeedback: false,
  })

  const actualPerspectives = computed(() =>
    props.perspectives
      .filter((p) => p.key || p.perspective)
      .map((p) =>
        p.perspective ? p.perspective : props.allPerspectives.find((pp) => pp.key === p.key)!,
      ),
  )

  const includePerspective = (
    perspective: DistributedPerspectiveAnalysis | PulsePerspectiveAnalysis,
  ) => props.showSelfFeedback || analysisPerspectiveKey(perspective) !== SELF_PERSPECTIVE_KEY

  const histogramDistributions = computed(() =>
    props.perspectives
      .filter(includePerspective)
      .map((p) => ({ key: analysisPerspectiveKey(p) ?? '', distribution: p.distribution })),
  )

  const { bars, barHeight } = useHistogramBars(
    () => props.scale,
    histogramDistributions,
    () => props.showSelfFeedback,
  )

  const valueBarPerspectives = computed(() =>
    toValueBarPerspectives(props.perspectives, props.allPerspectives),
  )
</script>
